// src/components/Signup/PersonalDetailsStep.jsx

import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { AiOutlineLoading } from 'react-icons/ai';
// import { BiCheckCircle, BiError } from 'react-icons/bi';
import Button from '../Button';
import { verifyBrokerAgent } from '../../api/api';

const PersonalDetailsStep = ({ formData, onFormDataChange, onNext, onCancel }) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});
  const [hasErrors, setHasErrors] = useState(false);
  
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null); // null, 'success', 'error', 'not_found', 'already_registered'
  const [verificationData, setVerificationData] = useState(null);

  const handleInputChange = (field, value) => {
    // Reset verification status when user changes any field
    if (verificationStatus) {
      setVerificationStatus(null);
      setVerificationData(null);
    }

    onFormDataChange({ ...formData, [field]: value });
    
    // Clear errors when user starts typing
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
      setHasErrors(Object.keys(newErrors).length > 0);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName?.trim()) {
      newErrors.firstName = true;
    }
    
    if (!formData.lastName?.trim()) {
      newErrors.lastName = true;
    }
    
    if (!formData.email?.trim()) {
      newErrors.email = true;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = true;
    }

    setErrors(newErrors);
    const hasValidationErrors = Object.keys(newErrors).length > 0;
    setHasErrors(hasValidationErrors);
    return !hasValidationErrors;
  };

  // ✅ Re-enable verification function (simplified without brokerage)
  const verifyBrokerInformation = async () => {
    try {
      setIsVerifying(true);
      setVerificationStatus(null);
      setVerificationData(null);
      
      const brokerData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email
        // Remove brokerHouseContactId
      };
      
      const result = await verifyBrokerAgent(brokerData);
      
      if (result.isSuccess) {
        // API call succeeded - check if data was returned
        if (result.data && result.data.brokerAgentContactId) {
          // User found in system, check registration status
          if (result.data.isRegistered === true) {
            setVerificationStatus('already_registered');
            setVerificationData(null);
          } else {
            // User exists but not registered, proceed to next step
            setVerificationStatus('success');
            setVerificationData(null);
            
            // Store the broker agent contact ID for later use
            onFormDataChange({
              ...formData,
              brokerAgentContactId: result.data.brokerAgentContactId,
              verifiedFirstName: result.data.brokerAgentFirstName,
              verifiedLastName: result.data.brokerAgentLastName,
              verifiedEmail: result.data.brokerAgentEmail,
              verifiedBrokerHouseName: result.data.brokerHouseName
            });
            
            // Auto-proceed to next step after a short delay
            setTimeout(() => {
              onNext();
            }, 1500);
          }
        } else {
          // API succeeded but no user data returned - user not found
          setVerificationStatus('not_found');
          setVerificationData(null);
        }
      } else {
        // API call failed - check specific error types
        if (result.errorType === 'not_found') {
          setVerificationStatus('not_found');
          setVerificationData(null);
        } else if (result.errorType === 'already_registered') {
          setVerificationStatus('already_registered');
          setVerificationData(null);
        } else {
          setVerificationStatus('error');
          setVerificationData({ message: result.message });
        }
      }
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationStatus('error');
      setVerificationData(null);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleContinue = async () => {
    if (!validateForm()) {
      return;
    }

    // If verification was successful, proceed to next step
    if (verificationStatus === 'success') {
      onNext();
      return;
    }

    // Otherwise, verify the broker information
    await verifyBrokerInformation();
  };

  const handleProceedToLogin = () => {
    window.location.href = '/';
  };

  // const getStatusIcon = () => {
  //   switch (verificationStatus) {
  //     case 'success':
  //       return <BiCheckCircle className="text-green-500 text-lg" />;
  //     case 'error':
  //     case 'not_found':
  //       return <BiError className="text-red-500 text-lg" />;
  //     case 'already_registered':
  //       return <BiCheckCircle className="text-red-500 text-lg" />;
  //     default:
  //       return null;
  //   }
  // };

  const getStatusColor = () => {
    switch (verificationStatus) {
      case 'success':
        return 'text-green-600';
      case 'error':
      case 'not_found':
        return 'text-red-500';
      case 'already_registered':
        return 'text-red-500';
      default:
        return hasErrors ? 'text-red-500' : 'text-gray-500';
    }
  };

  // Dynamic message generation that responds to language changes
  const getDisplayMessage = () => {
    if (isVerifying) {
      return t('signup.personalDetails.verifying');
    }
    
    // Generate messages dynamically based on verification status
    if (verificationStatus) {
      switch (verificationStatus) {
        case 'success':
          return t('signup.personalDetails.verificationSuccessful');
        case 'already_registered':
          return t('signup.personalDetails.alreadyRegistered');
        case 'not_found':
          return t('signup.personalDetails.verificationFailed', { brokerageEmail: 'your lender' });
        case 'error':
          return verificationData?.message || t('signup.personalDetails.verificationError');
        default:
          break;
      }
    }
    
    if (hasErrors) {
      return t('signup.errors.allFieldsRequired');
    }
    
    return t('signup.personalDetails.requiredFieldsMessage');
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          {t('signup.personalDetails.header')}
        </h2>
        <p className="text-sm text-gray-600">
          {t('signup.personalDetails.subheading')}
        </p>
      </div>

      {/* Form */}
      <div className="space-y-4">
        <div className="flex gap-2 justify-between w-full">
          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="labels block text-sm font-semibold mb-1">
              {t('signup.personalDetails.firstName')} *
            </label>
            <input
              id="firstName"
              type="text"
              value={formData.firstName || ''}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.firstName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={t('signup.personalDetails.firstName')}
              disabled={isVerifying}
            />
          </div>

          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="labels block text-sm font-semibold mb-1">
              {t('signup.personalDetails.lastName')} *
            </label>
            <input
              id="lastName"
              type="text"
              value={formData.lastName || ''}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.lastName ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder={t('signup.personalDetails.lastName')}
              disabled={isVerifying}
            />
          </div>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="labels block text-sm font-semibold mb-1">
            {t('signup.personalDetails.email')} *
          </label>
          <input
            id="email"
            type="email"
            value={formData.email || ''}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={`min-w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder={t('signup.personalDetails.email')}
            disabled={isVerifying}
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        {verificationStatus === 'already_registered' ? (
          <>
            <Button
              variant="block"
              onClick={handleProceedToLogin}
              label={t('signin.signinBtn')}
            />
            <button
              onClick={() => {
                setVerificationStatus(null);
                setVerificationData(null);
              }}
              className="w-full px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Check Information
            </button>
          </>
        ) : (
          <>
            <Button
              variant="block"
              onClick={handleContinue}
              label={t('signup.personalDetails.continue')}
              disabled={isVerifying}
            />
            <button
              onClick={onCancel}
              className="w-full px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isVerifying}
            >
              {t('signup.personalDetails.cancel')}
            </button>
          </>
        )}
      </div>

      {/* Status Message */}
      <div className="mt-4 text-center">
        <div className={`text-xs flex items-center justify-center gap-2 ${getStatusColor()}`}>
          {isVerifying && <AiOutlineLoading className="animate-spin text-blue-600" />}
          {/* {getStatusIcon()} */}
          <span>{getDisplayMessage()}</span>
        </div>
      </div>
    </div>
  );
};

PersonalDetailsStep.propTypes = {
  formData: PropTypes.object.isRequired,
  onFormDataChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default PersonalDetailsStep;